import React, { createContext, useContext, ReactNode } from 'react';
import { useChat } from '../hooks/useChat';
import { useAuth } from '../hooks/useAuth';

interface Message {
  _id: string;
  senderId: string;
  content: string;
  messageType: 'text' | 'image' | 'file';
  fileUrl?: string;
  isRead: boolean;
  readAt?: Date;
  createdAt: Date;
  sender?: Record<string, unknown>;
}

interface Chat {
  _id: string;
  participants: Record<string, unknown>[];
  messages: Message[];
  lastMessage?: {
    content: string;
    senderId: string;
    timestamp: Date;
  };
  unreadCount: Map<string, number>;
  isActive: boolean;
}

interface ChatContextType {
  socket: Record<string, unknown>;
  connected: boolean;
  chats: Chat[];
  activeChat: Chat | null;
  onlineUsers: string[];
  typing: { [chatId: string]: string[] };
  loading: boolean;
  fetchChats: () => Promise<void>;
  setActiveChat: (chat: Chat | null) => void;
  joinChat: (chatId: string) => void;
  leaveChat: (chatId: string) => void;
  sendMessage: (chatId: string, content: string, messageType?: 'text' | 'image' | 'file', fileUrl?: string) => void;
  startTyping: (chatId: string) => void;
  stopTyping: (chatId: string) => void;
  handleTyping: (chatId: string) => void;
  markAsRead: (chatId: string) => void;
  deleteMessage: (chatId: string, messageId: string) => void;
  getOrCreateChat: (otherUserId: string) => Promise<Record<string, unknown>>;
  isUserOnline: (userId: string) => boolean;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  
  // Defensive check - only initialize chat if user exists and has _id
  const userId = user?._id || null;
  const chat = useChat(userId);

  return (
    <ChatContext.Provider value={chat}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};